import { streamSse } from '@recipes/api-client';
import type {
  ShoppingEvent,
  ShoppingHistoryEntry,
  ShoppingList,
  ShoppingListItem,
  ShoppingPreferences,
  ShoppingPresenceUser,
} from '@recipes/contracts';
import { useLiveQuery } from 'dexie-react-hooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/components/feedback/toaster';
import { useConfig, useFeature, useProfile } from '@/features/shared/hooks';
import { api, CLIENT_ID } from '@/lib/api';
import { useAuth } from '@/lib/auth/auth-provider';
import { authStore } from '@/lib/auth/auth-store';
import { useOnlineStatus } from '@/lib/hooks/use-online-status';
import { offlineDb } from '@/lib/offline/db';
import {
  DEFAULT_SHOPPING_PREFERENCES,
  applyShoppingEvent,
  localShoppingPreferences,
  onShoppingActionsDropped,
  shoppingSyncState,
  startShoppingSync,
  storeListDetail,
  storeShoppingPreferences,
  wipeShoppingCopy,
  type ShoppingSyncState,
} from './device-copy';
import { replayShoppingQueue } from './shopping-queue';

/** Starts the device-copy sync and the replay of queued changes once a user is signed in. */
export function useShoppingBoot(): void {
  const { status } = useAuth();
  const { t } = useTranslation();
  useEffect(() => {
    if (status !== 'authenticated') return;
    startShoppingSync(() => replayShoppingQueue());
    // A queued change whose target is gone is dropped – with a notice, never silently.
    return onShoppingActionsDropped((count) =>
      toast.show({ title: t('shopping.offline.dropped', { count }), durationMs: 8000 }),
    );
  }, [status, t]);
}

/**
 * Signing out removes the device copy – and refuses while changes made on this device have not
 * reached the server, unless the user confirms discarding them.
 */
export function useGuardedLogout(): () => Promise<void> {
  const { logout } = useAuth();
  const { t } = useTranslation();
  return useCallback(async () => {
    const waiting = await offlineDb.pendingShopping.count().catch(() => 0);
    if (waiting > 0 && !window.confirm(t('shopping.offline.signOutConfirm', { count: waiting })))
      return;
    try {
      await logout();
    } finally {
      await wipeShoppingCopy().catch(() => undefined);
    }
  }, [logout, t]);
}

/** Active lists from the device copy; `undefined` while the copy is being read. */
export function useLocalLists(): Array<ShoppingList & { pending: boolean }> | undefined {
  const rows = useLiveQuery(() => offlineDb.shoppingLists.toArray(), []);
  return useMemo(
    () =>
      rows
        ?.map((row) => ({ ...row.list, pending: Boolean(row.pending) }))
        .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)),
    [rows],
  );
}

export interface LocalListState {
  /** `undefined` while reading, `null` when the list is not on this device. */
  list: ShoppingList | null | undefined;
  items: ShoppingListItem[];
  /** Ids of items with a change that still waits for the server. */
  pendingIds: Set<string>;
}

export function useLocalList(listId: string): LocalListState {
  const state = useLiveQuery(async () => {
    const [row, items] = await Promise.all([
      offlineDb.shoppingLists.get(listId),
      offlineDb.shoppingItems.where('listId').equals(listId).toArray(),
    ]);
    return { row: row ?? null, items };
  }, [listId]);
  return useMemo(
    () => ({
      list: state === undefined ? undefined : (state.row?.list ?? null),
      items: state?.items.map((row) => row.item) ?? [],
      pendingIds: new Set(state?.items.filter((row) => row.pending).map((row) => row.id) ?? []),
    }),
    [state],
  );
}

export function useShoppingHistory(): ShoppingHistoryEntry[] {
  const rows = useLiveQuery(() => offlineDb.shoppingHistory.toArray(), []);
  return useMemo(() => (rows ?? []).map((row) => row.entry), [rows]);
}

/**
 * The user's shopping settings. Online the profile is the truth (and refreshes the device
 * copy); without a connection the copy from the last sync is used.
 */
export function useShoppingPreferences(): ShoppingPreferences {
  const local = useLiveQuery(() => localShoppingPreferences(), []);
  const { data: profile } = useProfile();
  const fromProfile = profile?.shoppingPreferences;
  useEffect(() => {
    if (fromProfile) void storeShoppingPreferences(fromProfile).catch(() => undefined);
  }, [fromProfile]);
  return fromProfile ?? local ?? DEFAULT_SHOPPING_PREFERENCES;
}

export function useShoppingSyncState(): ShoppingSyncState | undefined {
  return useLiveQuery(() => shoppingSyncState(), []);
}

export function usePendingShoppingCount(): number {
  return useLiveQuery(() => offlineDb.pendingShopping.count(), []) ?? 0;
}

const visible = () => typeof document === 'undefined' || document.visibilityState === 'visible';

function useVisible(): boolean {
  const [state, setState] = useState(visible);
  useEffect(() => {
    const update = () => setState(visible());
    document.addEventListener('visibilitychange', update);
    return () => document.removeEventListener('visibilitychange', update);
  }, []);
  return state;
}

export type LiveMode = 'live' | 'polling' | 'offline';

export interface ShoppingLive {
  mode: LiveMode;
  /** Other people who have this list open right now (stream only). */
  presence: ShoppingPresenceUser[];
  /** Items changed by someone else a moment ago – briefly highlighted. */
  highlighted: Set<string>;
  /** One polite announcement per burst of foreign changes. */
  announcement: string;
  refresh: () => Promise<void>;
}

const HIGHLIGHT_MS = 3000;
const ANNOUNCE_BATCH_MS = 1500;
const STREAM_RETRY_MS = 30_000;

/**
 * Keeps an open list current (docs/partial/shopping-list-extensions-spec.md 6.2): server-sent events
 * while the list is visible, polling when the stream cannot be established, nothing while the
 * tab is hidden. Events are applied to the device copy, the single source the page renders.
 */
export function useShoppingLive(
  listId: string,
  options: {
    onGone: () => void;
    describe: (event: Extract<ShoppingEvent, { type: 'item.upserted' }>) => string;
  },
): ShoppingLive {
  const online = useOnlineStatus();
  const isVisible = useVisible();
  const liveEnabled = useFeature('shopping_live');
  const { data: config } = useConfig();
  const pollMs = (config?.shopping?.pollIntervalSeconds ?? 15) * 1000;
  const [mode, setMode] = useState<LiveMode>(online ? 'polling' : 'offline');
  const [presence, setPresence] = useState<ShoppingPresenceUser[]>([]);
  const [highlighted, setHighlighted] = useState<Set<string>>(new Set());
  const [announcement, setAnnouncement] = useState('');
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const refresh = useCallback(async () => {
    try {
      const requestedAt = Date.now();
      await storeListDetail(await api.shopping.get(listId), { requestedAt });
    } catch (error) {
      const status = (error as { status?: number }).status;
      if (status === 404 || status === 403) {
        await applyShoppingEvent(listId, { type: 'access.revoked' });
        optionsRef.current.onGone();
      }
    }
  }, [listId]);

  const active = Boolean(listId) && online && isVisible;

  useEffect(() => {
    if (!active) {
      setMode(online ? 'polling' : 'offline');
      setPresence([]);
      return;
    }
    const controller = new AbortController();
    let cancelled = false;
    let pollTimer: ReturnType<typeof setInterval> | undefined;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let announceTimer: ReturnType<typeof setTimeout> | undefined;
    const pendingAnnouncements: string[] = [];

    const noteForeignChange = (event: Extract<ShoppingEvent, { type: 'item.upserted' }>) => {
      const id = event.item.id;
      setHighlighted((prev) => new Set(prev).add(id));
      setTimeout(
        () =>
          setHighlighted((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          }),
        HIGHLIGHT_MS,
      );
      pendingAnnouncements.push(optionsRef.current.describe(event));
      clearTimeout(announceTimer);
      announceTimer = setTimeout(() => {
        setAnnouncement(pendingAnnouncements.splice(0).join('. '));
      }, ANNOUNCE_BATCH_MS);
    };

    const startPolling = () => {
      if (pollTimer || cancelled) return;
      setMode('polling');
      setPresence([]);
      pollTimer = setInterval(() => void refresh(), pollMs);
    };
    const stopPolling = () => {
      clearInterval(pollTimer);
      pollTimer = undefined;
    };

    let attempts = 0;
    const connect = async () => {
      if (cancelled) return;
      attempts++;
      if (!liveEnabled) {
        startPolling();
        return;
      }
      let opened = false;
      try {
        const stream = streamSse<ShoppingEvent>(api.shopping.eventsUrl(listId), {
          getAccessToken: () => authStore.getState().accessToken,
          signal: controller.signal,
        });
        for await (const event of stream) {
          if (cancelled) return;
          if (!opened) {
            opened = true;
            stopPolling();
            setMode('live');
            // After a reconnect: whatever happened while we were not listening.
            if (attempts > 1) void refresh();
          }
          if (event.type === 'presence') {
            setPresence(event.users);
            continue;
          }
          const applied = await applyShoppingEvent(listId, event);
          if (applied === 'gone') {
            optionsRef.current.onGone();
            return;
          }
          if (applied === 'gap') void refresh();
          if (
            event.type === 'item.upserted' &&
            event.clientId !== CLIENT_ID &&
            applied !== 'ignored'
          )
            noteForeignChange(event);
        }
      } catch {
        // fall through to polling
      }
      if (cancelled || controller.signal.aborted) return;
      startPolling();
      retryTimer = setTimeout(() => void connect(), STREAM_RETRY_MS);
    };

    void refresh();
    void connect();

    return () => {
      cancelled = true;
      controller.abort();
      stopPolling();
      clearTimeout(retryTimer);
      clearTimeout(announceTimer);
    };
  }, [active, online, listId, liveEnabled, pollMs, refresh]);

  return { mode, presence, highlighted, announcement, refresh };
}
